import { typeMappingsFor } from './paths'
import { env } from './env'
import type { Jurisdiction } from './jurisdiction'
import type {
  AtRisClause,
  AtRisDecision,
  AtRisDecisionMetadata,
  AtRisLaw,
  INode,
} from './ris.types'
import type { IDecision } from './norms.types'
import {
  getCourtAbbreviation,
  getCourtPath,
} from './judiciaryMonitoring/judiciaryMonitoring'

import type { Source } from './norms'

// `urlify` lives in its own module to avoid a cycle with
// `judiciaryMonitoring.ts`, which is imported here AND uses urlify.
// Re-exported for backwards compatibility with `@shared/lib/utils` callers.
import {
  urlify,
  deterministicUrlify,
  resolveUrlify,
  uniqueAnchor,
} from './urlify'

export { urlify, deterministicUrlify, resolveUrlify, uniqueAnchor }

export const domains = {
  'gesetzefinden-at': {
    preview: 'https://dev.gesetzefinden.at',
    production: 'https://gesetzefinden.at',
    local: 'http://localhost:3001',
  },
  // Same Next.js app as `gesetzefinden-at`, deployed with
  // NEXT_PUBLIC_DEFAULT_JURISDICTION=de — hence the identical local port: only
  // one jurisdiction runs at a time in development.
  'gesetzefinden-de': {
    preview: 'https://dev.gesetzefinden.de',
    production: 'https://gesetzefinden.de',
    local: 'http://localhost:3001',
  },
  'codara-dashboard': {
    preview: 'https://dev-dashboard.codara.com',
    production: 'https://dashboard.codara.com',
    local: 'http://localhost:3000',
  },
  'internal-dashboard': {
    preview: 'https://dev-internal.codara.com', // adjust URL
    production: 'https://admin.codara.com', // adjust URL
    local: 'http://localhost:3002', // adjust port
  },
}

export const isDev = env.NEXT_PUBLIC_VERCEL_ENV !== 'production'

export function getDomainFor(appName: keyof typeof domains): string {
  if (env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
    return domains[appName].production
  }
  if (
    env.NEXT_PUBLIC_VERCEL_ENV === 'preview' &&
    env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'dev'
  ) {
    return domains[appName].preview
  }
  // Use http for local server-to-server calls to avoid self-signed cert issues
  return domains[appName].local
}

export const domain =
  env.NEXT_PUBLIC_VERCEL_ENV === 'production'
    ? domains[env.NEXT_PUBLIC_APP_NAME as keyof typeof domains].production
    : env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
      ? env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'dev'
        ? domains[env.NEXT_PUBLIC_APP_NAME as keyof typeof domains].preview
        : `https://${env.NEXT_PUBLIC_VERCEL_BRANCH_URL || env.NEXT_PUBLIC_VERCEL_URL}`
      : domains[env.NEXT_PUBLIC_APP_NAME as keyof typeof domains].local

/**
 * Law/decision pages live on GesetzeFinden — every other app (dashboards,
 * backend) must link there with an absolute URL. Branches on the app name
 * rather than sniffing `domain`: feature-branch preview deployments live on
 * *.vercel.app domains, which a domain check misses — links then stayed
 * relative and 404'd on the dashboard.
 */
function toGesetzefindenUrl(path: string, jurisdiction?: Jurisdiction): string {
  // GesetzeFinden.at and .de are the same app deployed twice, so the target
  // site follows the norm's jurisdiction rather than `NEXT_PUBLIC_APP_NAME`
  // (which reads 'gesetzefinden-at' on both deployments). EU norms are
  // published on both sites, as are norms with no jurisdiction to go on, so
  // those stay wherever the caller is deployed.
  const localJurisdiction = env.NEXT_PUBLIC_DEFAULT_JURISDICTION ?? 'at'
  const targetJurisdiction =
    jurisdiction === 'at' || jurisdiction === 'de'
      ? jurisdiction
      : localJurisdiction
  if (
    env.NEXT_PUBLIC_APP_NAME.startsWith('gesetzefinden') &&
    targetJurisdiction === localJurisdiction
  ) {
    return path
  }
  const gesetzefinden =
    domains[
      targetJurisdiction === 'de' ? 'gesetzefinden-de' : 'gesetzefinden-at'
    ]
  // Like `domain`/`getDomainFor` above: anything that isn't explicitly
  // production or preview (undefined, 'development') counts as local.
  const gesetzefindenDomain =
    env.NEXT_PUBLIC_VERCEL_ENV === 'production'
      ? gesetzefinden.production
      : env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
        ? gesetzefinden.preview
        : gesetzefinden.local
  return `${gesetzefindenDomain}${path}`
}

export const DAY_IN_SECONDS = 60 * 60 * 24 // One day in seconds
export const ONE_HOUR = 60 * 60

interface Options {
  /**
   * Law abbreviation or normId used as the URL slug
   *
   * @example "ABGB"
   * @example "10002296"
   */
  norm?: string
  /**
   * The law's normId, appended as an `?id=` query param. Abbreviations are
   * not guaranteed to be unique, so links should always carry the normId;
   * the law page strips it client-side when the abbreviation is unambiguous.
   */
  normId?: string
  /** Enumeration as a URL path segment (clause pages) */
  enumeration?: string
  /**
   * Pre-computed clause slug from the DB. Takes priority over `enumeration`
   * for the path segment and is used as-is (already urlified + deduplicated).
   */
  clauseSlug?: string
  /** Enumeration as a URL hash (scroll-to on law page) */
  enumerationHash?: string
  lowerCase?: boolean
  /**
   * The norm's jurisdiction, resolving both the lawType→path table and the
   * GesetzeFinden site the link points at. Required to link a DE norm from an
   * AT deployment (or vice versa) — without it the local jurisdiction's table
   * is used and an out-of-jurisdiction `category` throws NO_MAPPING.
   */
  jurisdiction?: Jurisdiction | null
}

/**
 * Public URL of a draft document. parlament.gv.at `sourceUrl`s are stored
 * root-relative and need the host prefixed; absolute URLs pass through.
 */
export function publicDocumentUrl(sourceUrl: string): string
export function publicDocumentUrl(
  sourceUrl: string | undefined,
): string | undefined
export function publicDocumentUrl(
  sourceUrl: string | undefined,
): string | undefined {
  return sourceUrl?.startsWith('/')
    ? `https://parlament.gv.at${sourceUrl}`
    : sourceUrl
}

/**
 * Clickable URL for a gazette source document. RO gazettes resolve to the
 * mirror we stored at ingestion (`/api/gazette-pdf`): Monitorul Oficial serves
 * its PDFs only to requests carrying its own Referer, so a browser click on the
 * raw URL bounces to the home page. Every other gazette keeps its source URL.
 * Pass `origin` (the dashboard domain) for links that leave the app, e.g. in
 * emails.
 */
export function gazettePublicUrl(
  sourceUrl: string,
  options: { normId: string; origin?: string },
): string
export function gazettePublicUrl(
  sourceUrl: string | null | undefined,
  options: { normId: string; origin?: string },
): string | undefined
export function gazettePublicUrl(
  sourceUrl: string | null | undefined,
  { normId, origin = '' }: { normId: string; origin?: string },
): string | undefined {
  if (isMirroredGazetteNormId(normId)) {
    return `${origin}/api/gazette-pdf/${encodeURIComponent(normId)}`
  }
  return sourceUrl ?? undefined
}

/**
 * Whether a gazette's PDF is mirrored to S3 and therefore servable by
 * `/api/gazette-pdf`. Only the RO ingestion writes a mirror today, under the
 * one id shape `issueKeyToNormId` produces.
 *
 * The route guards on this same predicate before it reaches the database, so
 * the two cannot drift: anything this accepts must be a row the route can look
 * up, and anything it rejects is refused there without a query.
 */
export function isMirroredGazetteNormId(normId: string): boolean {
  return /^ro\.mo-pi-\d{4}-\d{1,5}(bis)?$/.test(normId)
}

/**
 * Builds a full URL path for a given category
 *
 * @example Category "V" gives "/bundesrecht/verordnungen"
 * @example Category "BG" gives "/bundesrecht/bundesgesetze"
 */
export function getUrl(
  category: string | string[],
  {
    norm,
    normId,
    enumeration,
    clauseSlug,
    enumerationHash,
    lowerCase = true,
    jurisdiction,
  }: Options = {},
): string {
  const mapping =
    typeof category === 'string'
      ? typeMappingsFor(jurisdiction)[category.toLowerCase()]
      : { category: category[0], subCategory: urlify(category[1] ?? '') }
  if (mapping === undefined) {
    throw Error(
      `NO_MAPPING - There is no mapping for category "${category.toString()}" (norm "${norm as string}")`,
    )
  }

  let normUrl = `/${mapping.category}/${mapping.subCategory}`
  if (!norm) {
    return normUrl
  }
  normUrl += `/${urlify(norm, { lowerCase })}`

  if (clauseSlug) {
    normUrl += `/${clauseSlug}`
  } else if (enumeration) {
    normUrl += `/${urlify(enumeration, { lowerCase })}`
  }

  // When the norm segment IS the normId (no abbreviation), the param would
  // be redundant — the segment already identifies the law unambiguously.
  if (normId && norm !== normId) {
    normUrl += `?id=${encodeURIComponent(normId)}`
  }

  if (!clauseSlug && !enumeration && enumerationHash) {
    normUrl += `#${urlify(enumerationHash)}`
  }

  return toGesetzefindenUrl(normUrl, jurisdiction ?? undefined)
}

const jurisdictionsWithGesetzeFindenPages: ReadonlySet<Jurisdiction> = new Set([
  'at',
  'de',
  'eu',
])

/**
 * Whether GesetzeFinden has a page for this law, or it has to be linked at the
 * portal that published it.
 *
 * `paths.ts` holds path tables for AT, DE and EU only, so a law from any other
 * jurisdiction has nowhere to land: `typeMappingsFor` would answer with the
 * deployment's own AT/DE table and file the act under a path that isn't its.
 *
 * German state law has none either. `pathMappingsDE` holds only `bundesrecht`,
 * so `LG` — most of any state corpus — maps to nothing at all, and the state
 * types that do map (`RV`, `GG`, `VERTRAG`, …) would file a Landesverordnung
 * under `/bundesrecht/…`. Austrian state law is unaffected: `pathMappingsAT`
 * owns it under `landesrecht`.
 *
 * Drop the `state` arm once `pathMappingsDE` has a `landesrecht` category; add
 * a jurisdiction to the set once `paths.ts` gains its table.
 */
export function hasGesetzeFindenPage({
  jurisdiction,
  state,
}: {
  jurisdiction?: Jurisdiction | null
  state?: string | null
}): boolean {
  if (
    jurisdiction != null &&
    !jurisdictionsWithGesetzeFindenPages.has(jurisdiction)
  ) {
    return false
  }
  return !(jurisdiction === 'de' && state != null)
}

/**
 * Where a law link should point: its GesetzeFinden page, or the publisher's own
 * page where GesetzeFinden has none — state law, and any `lawType` its
 * jurisdiction's path table doesn't map. undefined when the law has neither, so a
 * caller can render plain text rather than a dead link.
 *
 * @param options as {@link getUrl} takes them, minus the jurisdiction, which is
 *   the law's own. Pass a `norm`: without one the GesetzeFinden branch answers
 *   with the law type's category page rather than the law.
 */
export function lawUrl(
  law: {
    lawType?: string | null
    jurisdiction?: Jurisdiction | null
    state?: string | null
    sourceUrl?: string | null
  },
  options: Omit<Options, 'jurisdiction'>,
): string | undefined {
  if (law.lawType && hasGesetzeFindenPage(law)) {
    try {
      return getUrl(law.lawType, { ...options, jurisdiction: law.jurisdiction })
    } catch {
      // NO_MAPPING — a lawType its own jurisdiction's table doesn't know.
    }
  }
  return law.sourceUrl ?? undefined
}

export function lawHitUrl(
  hit: Parameters<typeof lawUrl>[0] & {
    normId: string
    abbreviation?: string | null
  },
  enumeration?: string,
) {
  return lawUrl(hit, {
    norm: hit.abbreviation ?? hit.normId,
    normId: hit.normId,
    enumeration,
  })
}

/**
 * Builds the full URL for a decision's detail page on gesetzefinden.at.
 * Uses the raw institution to determine the correct path prefix
 * (`/judikatur/` for AT, `/eu-judikatur/` for EU).
 *
 * Falls back to the decision's `sourceUrl` (or `'#'`) when no
 * institution-related field is present, so callers don't render broken links
 * like `/judikatur//xxx` for malformed records.
 *
 * @example
 * getDecisionUrl({ institution: 'Vwgh', normId: 'at.123456', ... })
 * // '/judikatur/vwgh/at123456'
 *
 * getDecisionUrl({ institution: 'Court of Justice', normId: 'eu.62020CJ0123', ... })
 * // '/eu-judikatur/gerichtshof/eu.62020cj0123'
 */
export function getDecisionUrl(decision: IDecision) {
  const institution = getRawInstitution(decision)
  if (!institution) return decision.sourceUrl ?? '#'

  const url = `${getCourtPath(institution)}/${urlify(decision.normId)}`
  return toGesetzefindenUrl(url)
}

/** Minimal shape required to determine a decision's institution/court. */
export type JudikaturApplikationInput = {
  institution?: IDecision['institution']
  Applikation?: AtRisDecisionMetadata['Applikation']
  Gericht?: AtRisDecisionMetadata['Gericht']
  Organ?: AtRisDecisionMetadata['Organ']
}

/**
 * Extracts the raw institution string from a decision object, without mapping
 * it to an abbreviation. Returns e.g. "Court of Justice", "vwgh".
 * Handles legacy AT-RIS fields (Applikation, Gericht, Organ) as fallbacks.
 *
 * Separate from {@link getJudikaturApplikation} because {@link getDecisionUrl}
 * needs the unmapped value to look up the EU URL path via {@link getCourtPath}.
 */
function getRawInstitution(decision: JudikaturApplikationInput): string {
  const application =
    (typeof decision.institution === 'string'
      ? decision.institution
      : (decision.institution as string[] | undefined)?.[0]) ??
    // Keep as a legacy fallback; after migration this should be removed
    (decision.Applikation !== 'Justiz'
      ? decision.Applikation?.toLowerCase()
      : (decision.Gericht ?? decision.Organ))
  return application ?? ''
}

/**
 * Returns the display abbreviation for a decision's court/institution, or
 * `undefined` when no institution-related field is set (callers like
 * `getNorm`/`ris` rely on this nullish branch — do not throw).
 *
 * @example
 * getJudikaturApplikation({ institution: 'Court of Justice', ... }) // 'EuGH'
 * getJudikaturApplikation({ Applikation: 'Vwgh', ... })             // 'VwGH'
 * getJudikaturApplikation({})                                       // undefined
 */
export function getJudikaturApplikation(
  decision: JudikaturApplikationInput,
): string | undefined {
  const application = getRawInstitution(decision)
  if (!application) return undefined
  return getCourtAbbreviation(application)
}

function coalesce<T>(primary?: T, fallback?: T) {
  return primary ?? fallback
}

function normalizeJudikaturDecisionSourceMetadata(
  data: Omit<AtRisDecision, 'source'> & AtRisDecisionMetadata,
): AtRisDecisionMetadata {
  const sourceMetadata = data.sourceMetadata
  return {
    ...sourceMetadata,
    ID: sourceMetadata?.ID ?? data.ID ?? '',
    Entscheidungstexte: coalesce(
      sourceMetadata?.Entscheidungstexte,
      data.Entscheidungstexte,
    ),
    Anmerkung: coalesce(sourceMetadata?.Anmerkung, data.Anmerkung),
    Rechtssatznummer: coalesce(
      sourceMetadata?.Rechtssatznummer,
      data.Rechtssatznummer ?? data.Rechtssatznummern?.[0],
    ),
    Rechtssatznummern: coalesce(
      sourceMetadata?.Rechtssatznummern,
      data.Rechtssatznummern,
    ),
  }
}
// Legacy `source` values produced by older ingestion paths; mapped to the
// current canonical jurisdiction-prefixed names.
const legacySourceMap: Partial<Record<Source, Source>> = {
  findok: 'at.findok',
  ris: 'at.ris',
  eurlex: 'eu.eurlex',
}

// Sources for which the AT-RIS field-renaming pass (Applikation->institution,
// Entscheidungsdatum->decisionDate, etc.) makes sense. EU/Findok/Parlament/de.ris
// data does not have AT-RIS-shaped legacy metadata to map.
const atRisLikeSources: Source[] = ['ris', 'at.ris']

// FIXME: not only decision
export function normalizeJudikaturDecisionData(
  data: (IDecision | (Omit<IDecision, 'source'> & { source: Source })) &
    AtRisDecisionMetadata,
): IDecision {
  const source = (legacySourceMap[data.source] ??
    data.source) as IDecision['source']

  // Non-AT-RIS sources still get the source label rewritten so downstream
  // consumers can rely on the canonical `at.*` / `eu.*` form, but skip the
  // AT-RIS-specific field renaming below.
  if (data.source && !atRisLikeSources.includes(data.source)) {
    return { ...data, source } as IDecision
  }

  const documentType =
    data.documentType ?? getDocumentType(data.Dokumenttyp ?? '')
  // After the early-return above, data.source is one of `atRisLikeSources`,
  // i.e. effectively `AtRisDecision`-shaped — assert that so the
  // AT-RIS-specific normalizer accepts it.
  const sourceMetadata = normalizeJudikaturDecisionSourceMetadata(
    data as Omit<AtRisDecision, 'source'> & AtRisDecisionMetadata,
  )

  return {
    ...data,
    source,
    sourceMetadata,
    institution: coalesce(
      data.institution,
      data.Applikation !== 'Justiz'
        ? data.Applikation
        : (data.Gericht ?? data.Organ),
    ),
    documentType,
    caseNumber: coalesce(
      data.caseNumber,
      data.Geschaeftszahl ?? data.Geschaeftszahlen?.[0],
    ),
    altCaseNumbers: coalesce(data.altCaseNumbers, data.Geschaeftszahlen),
    sourceUrl: coalesce(data.sourceUrl, data.DokumentUrl),
    decisionType: coalesce(data.decisionType, data.Entscheidungsart),
    decisionDate: coalesce(data.decisionDate, data.Entscheidungsdatum),
    publishedDate: coalesce(
      data.publishedDate,
      data.Veroeffentlicht ?? data.created,
    ),
    changedDate: coalesce(data.changedDate, data.Geaendert),
  }
}

export function normalizeJudikaturLawData<T extends AtRisLaw | AtRisClause>(
  data: T,
): T {
  const documentType =
    data.documentType ?? getDocumentType(data.Dokumenttyp ?? '')

  return {
    ...data,
    documentType,
    title: coalesce(data.title, data.Kurztitel ?? data.Titel ?? data.Langtitel),
    longTitle: coalesce(data.longTitle, data.Langtitel ?? data.Titel),
    abbreviation: coalesce(data.abbreviation, data.Abkuerzung),
    inEffectDate: coalesce(
      data.inEffectDate,
      data.Inkrafttretedatum ?? data.Inkrafttretensdatum,
    ),
    outOfEffectDate: coalesce(
      data.outOfEffectDate,
      data.Ausserkrafttretedatum ?? data.Ausserkrafttretensdatum,
    ),
    publishedDate: coalesce(data.publishedDate, data.Veroeffentlicht),
    changedDate: coalesce(data.changedDate, data.Geaendert),
    sourceUrl: coalesce(
      data.sourceUrl,
      data.GesamteRechtsvorschriftUrl ?? data.DokumentUrl ?? data.risUrl,
    ),
  }
}

const formatter = new Intl.DateTimeFormat('de', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})
export function formatDate(date?: string | Date | null): string {
  const d = date instanceof Date ? date : date ? new Date(date) : new Date()
  if (!Number.isFinite(d.getTime())) return '—'
  return formatter.format(d)
}

// A calendar day stored as UTC midnight: formatting it in the viewer's
// timezone would show the previous day west of UTC.
const calendarDateFormatter = new Intl.DateTimeFormat('de-AT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
})
/** DD.MM.YYYY, in every language. Accepts the ISO string a cached read leaves. */
export function formatCalendarDate(date: string | Date): string {
  return calendarDateFormatter.format(new Date(date))
}

const formatter1 = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
})
const formatter2 = new Intl.NumberFormat('de', {
  maximumFractionDigits: 0,
})
export function formatNumber(number: number, { short } = { short: false }) {
  if (short) {
    return formatter1.format(number).replace('.', ',')
  }

  return formatter2.format(number)
}

let visitorId: string | undefined
/**
 * Returns the visitor ID from Matomo (also known as UserID)
 * See https://forum.matomo.org/t/how-can-i-retrieve-visitor-id-from-javascript/16345
 * And https://forum.matomo.org/t/retrieve-visitor-id-for-gdpr-requests/38332/2
 */
export async function getVisitorId(): Promise<string> {
  if (visitorId) return visitorId

  // @ts-expect-error global var
  await window.mtmReady

  return await new Promise((resolve) => {
    ;(
      window as unknown as Window & typeof globalThis & { _paq?: unknown[] }
    )._paq?.push([
      function (this: { getVisitorId: () => string }) {
        visitorId = this.getVisitorId()
        resolve(visitorId)
      },
    ])
  })
}

// From https://stackoverflow.com/a/58110124
export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined
}

/**
 * Returns a pure text representation of the content item
 * From https://github.com/syntax-tree/nlcst-to-string/blob/main/index.js
 */
export function toString(
  node: string | string[] | INode[] | INode,
  separator = '',
): string {
  if (node === undefined || node === null) {
    console.error(
      `Expected node, not ${JSON.stringify(node)} (type ${typeof node})`,
    )
    throw new Error(
      `Expected node, not ${JSON.stringify(node)} (type ${typeof node})`,
    )
  }

  if (typeof node === 'string') return node
  else if (Array.isArray(node)) {
    return node.map((child) => toString(child, separator)).join(separator)
  } else if (Array.isArray(node.children)) {
    return node.children
      .map((child) => toString(child, separator))
      .join(separator)
  } else if (typeof node.value === 'string') return node.value
  else return ''
}

/**
 * Splits an array into groups
 *
 * @see https://stackoverflow.com/a/64489535/8464260
 * @param array The array to apply grouping to
 * @param groupingFn A function to decide which group to put an item into. Is called for every item in the array, the returned string is used as the group key which the item should be grouped with.
 * @returns An object with the groups
 */
export const groupBy = <T>(array: T[], groupingFn: (v: T) => string) => {
  return array.reduce<Record<string, T[]>>((acc, value) => {
    ;(acc[groupingFn(value)] ||= []).push(value)
    return acc
  }, {})
}

/**
 * Based on https://stackoverflow.com/a/9229821
 * @param a
 * @param key
 * @returns
 */
export function uniqueBy<T>(a: T[], key: (arg: T) => string) {
  const seen: Record<string, boolean> = {}
  return a.filter(function (item) {
    const k = key(item)
    return Object.prototype.hasOwnProperty.call(seen, k)
      ? false
      : (seen[k] = true)
  })
}

export function endsInYearNumber(str?: string) {
  if (!str) {
    return false
  }
  const splits = str.split(' ')
  const lastSplit = splits[splits.length - 1]
  if (lastSplit && !Number.isNaN(Number(lastSplit)) && lastSplit.length === 4) {
    return str.substring(0, str.length - 4).trim()
  }
  return false
}

export function getDocumentType(risType: string) {
  switch (risType) {
    case 'Norm':
      return 'law'
    case 'Paragraf':
    case 'Paragraph':
      return 'clause'
    // return 'law'
    case 'Text':
      return 'decision'
    case 'decision':
      return 'decision'
    case 'Rechtssatz':
      return 'rechtssatz'
    default:
      return risType
  }
}

export function getRechtssatzCaseNumber(decision: AtRisDecision) {
  const rechtssatznummer = getRechtssatznummer(decision)
  const caseNumber = decision.caseNumber ?? decision.altCaseNumbers?.[0]
  return Number(rechtssatznummer) < 100
    ? `${caseNumber ?? ''} ${rechtssatznummer ?? ''}`
    : (rechtssatznummer ?? caseNumber)
}

export function getRechtssatznummer(decision: AtRisDecision) {
  return (
    decision.sourceMetadata?.Rechtssatznummer ??
    decision.sourceMetadata?.Rechtssatznummern?.[0]
  )
}

export function sanitizeFileName(fileName: string) {
  return fileName
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .trim()
}

export { ensureError } from './errors'
